import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { AuthProvider } from "./module_roots/AuthContext";
import PrivateRoute from "./module_roots/PrivateRoute";

// Lazy load heavy components
const Login = lazy(() => import("./module_roots/Login"));
const DashboardSuperAdmin = lazy(() => import("./module_superadmin/Dashboard/Dashboard_SuperAdmin"));
const DashboardPOS = lazy(() => import("./module_salanty/Dashboard/Dashboard_POS"));
const DashboardPO = lazy(() => import("./module_salanty/Dashboard/Dashboard_PO"));
const DashboardFabrics = lazy(() => import("./module_fabrics/Dashboard/Dashboard_Fabrics"));
const DashboardMechanicsTH = lazy(() => import("./module_mechanics/Dashboard/Dashboard_MechanicsTH"));
const DashboardMechanicsAP = lazy(() => import("./module_mechanics/Dashboard/Dashboard_MechanicsAP"));
const DashboardHardwareAP = lazy(() => import("./module_hardware/Dashboard/Dashboard_HardwareAP"));
const DashboardHardwareTH = lazy(() => import("./module_hardware/Dashboard/Dashboard_HardwareTH"));
const DashboardAutosAP = lazy(() => import("./module_autos/Dashboard/Dashboard_AutosAP"));
const DashboardAutosTH = lazy(() => import("./module_autos/Dashboard/Dashboard_AutosTH"));
const DashboardPharmacy = lazy(() => import("./module_pharmacy/Dashboard/Dashboard_Pharmacy"));
const DashboardPharmacyTAX = lazy(() => import("./module_pharmacy/Dashboard/Dashboard_PharmacyTAX"));
const DashboardBusinessModule = lazy(() => import("./module_cosmetics/Dashboard/Dashboard_BusinessModule"));
const DashboardElectronics = lazy(() => import("./module_electronics/Dashboard/Dashboard_Electronics"));
const DashboardSolar = lazy(() => import("./module_solar/Dashboard/Dashboard_Solar"));
const LedgerView = lazy(() => import("./module_cosmetics/LedgerView"));
const SellList = lazy(() => import("./module_fabrics/Fab_SaleList"));
const DashboardLayout = lazy(() => import("./module_superadmin/Dashboard/DashboardLayout"));

// Route configuration for better organization
const ROUTE_CONFIG = {
  superAdmin: {
    path: "/DashboardSuperAdmin",
    component: DashboardSuperAdmin,
    roles: ["super_admin"]
  },
  fabrics: {
    path: "/DashboardFabrics",
    component: DashboardFabrics,
    roles: ['admin_fabrics', 'manager_fabrics', 'user_fabrics', 'viewer_fabrics']
  },
  mechanicsTh: {
    path: "/DashboardMechanicsTh",
    component: DashboardMechanicsTH,
    roles: ['admin_mechanicsth', 'manager_mechanicsth', 'user_mechanicsth', 'viewer_mechanicsth']
  },
  mechanicsAp: {
    path: "/DashboardMechanicsAp",
    component: DashboardMechanicsAP,
    roles: ['admin_mechanicsap', 'manager_mechanicsap', 'user_mechanicsap', 'viewer_mechanicsap']
  },
  hardwareAp: {
    path: "/DashboardHardwareAp",
    component: DashboardHardwareAP,
    roles: ['admin_hardwareap', 'manager_hardwareap', 'user_hardwareap', 'viewer_hardwareap']
  },
  hardwareTh: {
    path: "/DashboardHardwareTh",
    component: DashboardHardwareTH,
    roles: ['admin_hardwareth', 'manager_hardwareth', 'user_hardwareth', 'viewer_hardwareth']
  },
  autosAp: {
    path: "/DashboardAutosAp",
    component: DashboardAutosAP,
    roles: ['admin_autosap', 'manager_autosap', 'user_autosap', 'viewer_autosap']
  },
  autosTh: {
    path: "/DashboardAutosTh",
    component: DashboardAutosTH,
    roles: ['admin_autosth', 'manager_autosth', 'user_autosth', 'viewer_autosth']
  },
  pharmacy: {
    path: "/DashboardPharmacy",
    component: DashboardPharmacy,
    roles: ['admin_pharmacy', 'manager_pharmacy', 'user_pharmacy', 'viewer_pharmacy']
  },
  pharmacyTax: {
    path: "/DashboardTxPharmacy",
    component: DashboardPharmacyTAX,
    roles: ['admin_pharmacytax', 'manager_pharmacytax', 'user_pharmacytax', 'viewer_pharmacytax']
  },
  business: {
    path: "/BusinessDashboard",
    component: DashboardBusinessModule,
    roles: ['admin_cosmetics', 'manager_cosmetics', 'user_cosmetics', 'viewer_cosmetics']
  },
  solar: {
    path: "/DashboardSolar",
    component: DashboardSolar,
    roles: ['admin_solar', 'manager_solar', 'user_solar', 'viewer_solar']
  },
  electronics: {
    path: "/DashboardElectronics",
    component: DashboardElectronics,
    roles: ['admin_electronics', 'manager_electronics', 'user_electronics', 'viewer_electronics']
  }
};

// Helper to create route with subroutes
const createRoute = (config) => {
  const RouteComponent = config.component;
  return (
    <Route
      path={`${config.path}/*`}
      element={
        <PrivateRoute allowedRoles={config.roles}>
          <RouteComponent />
        </PrivateRoute>
      }
    />
  );
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        {/*
          Wrapping Routes in <main> fixes the "Document does not have a
          main landmark" accessibility audit. This is a semantic landmark
          only — it adds zero visual/layout change, so it won't touch CLS.
        */}
        <main>
          <Suspense fallback={null}> {/* ← REMOVED the blue loader */}
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<Login />} />
              <Route path="/login" element={<Login />} />
              <Route path="/ledger" element={<LedgerView />} />

              {/* Super Admin */}
              <Route path="/DashboardSuperAdmin/*" element={
                <PrivateRoute requiredRole="super_admin">
                  <DashboardSuperAdmin />
                </PrivateRoute>
              } />

              {/* Dynamically generate routes */}
              {Object.values(ROUTE_CONFIG).map((config, index) => (
                <React.Fragment key={index}>
                  {createRoute(config)}
                </React.Fragment>
              ))}

              {/* Fabrics Sales */}
              <Route path="/dashboard/fab-sales" element={
                <PrivateRoute>
                  <SellList />
                </PrivateRoute>
              } />

              {/* Legacy fallback */}
              <Route path="/Dashboard/*" element={
                <PrivateRoute>
                  <DashboardLayout />
                </PrivateRoute>
              } />

              {/* Catch all */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </Suspense>
        </main>
      </Router>
    </AuthProvider>
  );
};

export default App;